import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import t from '@i18n/t';
import { RootState } from '@state/types';
import { settingsActions } from '@state/settings';
import colors from '@utils/colors';
import { RegularText, SmallText } from '@utils/typography';
import { ownFreeTimeColor } from '@utils/theme/freeTimeColor';
import { hideAllTippies } from '@state/common/utils';
import ColorPickerTippy from '../../common/colorPicker/ColorPickerTippy';
import { ColorItem } from '../../common/colorPicker/ColorPicker';
import { Row } from '../../common/layoutUtils';

/**
 * Free study time's colour, apart from the accent (Petr, 2026-10-15: a student's colour coding clashed with it). The
 * picker's Default is the accent; any other colour is saved to the account and drawn on the calendar's free time and
 * in the week donut and Analytics (freeTimeColors).
 */
const FreeTimeColorPicker: FC = () => {
  const dispatch = useDispatch();
  const own = ownFreeTimeColor(useSelector((state: RootState) => state.settings.userSettings?.freeTimeColor));
  const shown = own ?? colors.primaryPurple;

  const save = (color: string, isDefault?: boolean) => {
    const freeTimeColor = isDefault || color.toUpperCase() === colors.primaryPurple.toUpperCase() ? undefined : color;
    dispatch(settingsActions.saveUserSettings.request({ freeTimeColor }));
    hideAllTippies();
  };

  return (
    <Wrapper>
      <ColorPickerTippy
        defaultColor={colors.primaryPurple}
        defaultSelected={!own}
        selectedColor={shown}
        onSave={save}
        target={color => (
          <Row>
            <ColorItem color={color || shown} size={18} used={false} selected={false} borderRadius={4} />
            <RegularText style={{ marginLeft: 8 }}>{own ? t.FREE_TIME_COLOR_OWN : t.FREE_TIME_COLOR_ACCENT}</RegularText>
          </Row>
        )}
      />
      <Hint>{t.FREE_TIME_COLOR_HINT}</Hint>
    </Wrapper>
  );
};

export default FreeTimeColorPicker;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-top: 10px;
`;

const Hint = styled(SmallText)`
  color: ${({ theme }) => theme.disabledTextColor};
`;
